import _slicedToArray from "/Users/werk/Documents/Sites/bouncespace/www/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["children", "className", "component", "columnSpan", "defaultHeight"];
import * as React from 'react';
import clsx from 'clsx';
import { createUnarySpacing, getValue, handleBreakpoints, unstable_resolveBreakpointValues as resolveBreakpointValues } from '@mui/system';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import { unstable_composeClasses as composeClasses } from '@mui/core';
import { styled, useThemeProps, useTheme } from '@mui/material/styles';
import { getMasonryItemUtilityClass } from './masonryItemClasses';
import MasonryContext from '../Masonry/MasonryContext';
import { jsx as _jsx } from "react/jsx-runtime";

var useUtilityClasses = function useUtilityClasses(ownerState) {
  var classes = ownerState.classes;
  var slots = {
    root: ['root']
  };
  return composeClasses(slots, getMasonryItemUtilityClass, classes);
};

export var style = function style(_ref) {
  var ownerState = _ref.ownerState,
      theme = _ref.theme;
  var styles = {
    width: '100%',
    '& > *': _extends({
      // all contents should have a width of 100%
      width: '100%',
      boxSizing: 'inherit'
    }, ownerState.hasDefaultHeight && {
      height: '100%'
    }),
    visibility: ownerState.height ? 'visible' : 'hidden',
    gridColumnEnd: "span ".concat(ownerState.columnSpan),
    boxSizing: 'inherit'
  };

  if (Array.isArray(ownerState.spacing) || typeof ownerState.spacing === 'object') {
    var base = {};
    Object.keys(theme.breakpoints.values).forEach(function (breakpoint) {
      if (ownerState.spacing[breakpoint] != null) {
        base[breakpoint] = true;
      }
    });
    var spacingValues = resolveBreakpointValues({
      values: ownerState.spacing,
      base: base
    });
    var transformer = createUnarySpacing(theme);

    var styleFromPropValue = function styleFromPropValue(propValue) {
      var gap = ownerState.height ? Number(getValue(transformer, propValue).replace('px', '')) : 0; // For lazy-loaded images to load properly, masonry item should take up space greater than 1px.
      // Taking into account a row gap of 2px, rowSpan should at least be 2.

      var rowSpan = ownerState.height ? Math.ceil((ownerState.height + gap) / 2) : 2;
      return {
        gridRowEnd: "span ".concat(rowSpan),
        paddingBottom: gap === 0 ? 0 : gap - 2
      };
    };

    styles = _extends({}, styles, handleBreakpoints({
      theme: theme
    }, spacingValues, styleFromPropValue));
  }

  return styles;
};
var MasonryItemRoot = styled('div', {
  name: 'MuiMasonryItem',
  slot: 'Root',
  overridesResolver: function overridesResolver(props, styles) {
    return [styles.root];
  }
})(style);
var MasonryItem = /*#__PURE__*/React.forwardRef(function MasonryItem(inProps, ref) {
  var props = useThemeProps({
    props: inProps,
    name: 'MuiMasonryItem'
  });
  var masonryItemRef = React.useRef(null);

  var _React$useContext = React.useContext(MasonryContext),
      _React$useContext$spa = _React$useContext.spacing,
      spacing = _React$useContext$spa === void 0 ? 1 : _React$useContext$spa;

  var children = props.children,
      className = props.className,
      _props$component = props.component,
      component = _props$component === void 0 ? 'div' : _props$component,
      _props$columnSpan = props.columnSpan,
      columnSpan = _props$columnSpan === void 0 ? 1 : _props$columnSpan,
      defaultHeight = props.defaultHeight,
      other = _objectWithoutPropertiesLoose(props, _excluded);

  var hasDefaultHeight = defaultHeight !== undefined;

  var _React$useState = React.useState(defaultHeight),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      height = _React$useState2[0],
      setHeight = _React$useState2[1];

  var ownerState = _extends({}, props, {
    spacing: spacing,
    hasDefaultHeight: hasDefaultHeight,
    columnSpan: columnSpan,
    height: height < 0 ? 0 : height // MasonryItems to which negative or zero height is passed will be hidden

  });

  var classes = useUtilityClasses(ownerState);
  React.useEffect(function () {
    // Do not create a resize observer in case of provided height masonry
    if (hasDefaultHeight) {
      return undefined;
    }

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    var resizeObserver = new ResizeObserver(function (_ref2) {
      var _ref3 = _slicedToArray(_ref2, 1),
          item = _ref3[0];

      setHeight(item.contentRect.height);
    });
    resizeObserver.observe(masonryItemRef.current.firstChild);
    return function () {
      resizeObserver.disconnect();
    };
  }, [hasDefaultHeight]);
  var handleRef = useForkRef(ref, masonryItemRef);
  var theme = useTheme();
  var styleProp = {};

  if (!Array.isArray(spacing) && typeof spacing !== 'object') {
    var gap = height ? Number(theme.spacing(spacing).replace('px', '')) : 0;
    var rowSpan = height ? Math.ceil((height + gap) / 2) : 2;
    styleProp.gridRowEnd = "span ".concat(rowSpan);
    styleProp.paddingBottom = gap === 0 ? 0 : gap - 2;
  }

  return /*#__PURE__*/_jsx(MasonryItemRoot, _extends({
    as: component,
    className: clsx(classes.root, className),
    ref: handleRef,
    ownerState: ownerState,
    style: styleProp
  }, other, {
    children: React.Children.only(children)
  }));
});
process.env.NODE_ENV !== "production" ? void 0 : void 0;
export default MasonryItem;